import React, {
  useCallback,
  useEffect,
  useReducer,
  useState,
  useContext,
  useMemo,
} from 'react';
import moment from 'moment';
import {
  GetLookupItemsByLookupTypeName,
  PropertiesAdvanceSearchTest,
  propertyDetailsPut,
} from '../../../../../Services';
import { useTranslate } from '../../../../../Hooks';
import CPP_BI_PropertySpecificationSection from './sections/CPP_BI_PropertySpecificationSection';
import CPP_BI_FinancialInformation from './sections/CPP_BI_FinancialInformation';
import CPP_BI_NonGCCbuyers from './sections/CPP_BI_NonGCCbuyers';
import CPP_PD_BasicInformationSection from './sections/CPP_PD_BasicInformationSection';
import * as Yup from 'yup';
import CPP_PropertyDetailsSkeleton from '../../../../../Components/V2/CPP_PropertyDetailsSkeleton/CPP_PropertyDetailsSkeleton';
import { CompletePropertyProfileContext } from '../../../../../Contexts/CompletePropertyProfileContext/CompletePropertyProfileContext';
import useStyles from './styles';
import { useVerticalNav } from '../../../../../Contexts/VerticalNavContext';
const defaultSelected = {
  propertyPlan: null,
  propertyType: null,
  propertyUsage: null,
  zoneType: null,
  ownership: null,
  community: null,
  urlLocation: null,
  numberOfFloors: null,
  completionDate: null,
  numberOfResidentialUnits: null,
  numberOfCommercialUnits: null,
  serviceCharge: null,
  expectedROI: null,
  securityDeposit: null,
  availableForNonGCCBuyer: null,
};

const defaultData = {
  propertyPlans: [],
  propertyTypes: [],
  propertyUsages: [],
  zoneTypes: [],
  ownerships: [],
  communities: [],
};

const filter = {
  pageIndex: 0,
  pageSize: 100,
};

function CPP_PropertyDetailsComponent({ propertyId, stepId, onStepComplete }) {
  const styles = useStyles();
  const { setAlertBoxContent } = useVerticalNav();
  const context = useContext(CompletePropertyProfileContext);
  const { translate } = useTranslate('CompletePropertyProfile');
  const reducer = useCallback((state, action) => {
    if (action.id !== 'edit') return { ...state, [action.id]: action.value };
    return {
      ...action.value,
    };
  }, []);
  const [data, setData] = useReducer(reducer, defaultData);
  const [selected, setSelected] = useReducer(reducer, defaultSelected);
  const [isLoading, setIsLoading] = useState(true);
  const [propertyDetails, setPropertyDetails] = useState({});
  const [isReady, setIsReady] = useState(false);
  const safeHandleDate = (dateValue) => {
    if (!dateValue) return null;

    if (moment.isMoment(dateValue)) return dateValue;

    if (dateValue instanceof Date) return moment(dateValue);

    if (typeof dateValue === 'string') {
      const parsedMoment = moment(dateValue);
      return parsedMoment.isValid() ? parsedMoment : null;
    }

    const momentDate = moment(dateValue);
    return momentDate.isValid() ? momentDate : null;
  };

  const safeHandleNonGCCBuyer = (value) => {
    if (value === null || value === undefined) return null;

    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase().trim();
      return lowerValue === 'yes' || lowerValue === 'true' ? 'Yes' : 'No';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'number') {
      return value > 0 ? 'Yes' : 'No';
    }

    return null;
  };

  const handleSelectedChange = (field, value) => {
    setSelected({ id: field, value: value });
  };

  const handleMultipleSelectedChange = (updates) => {
    setSelected({ id: 'edit', value: updates });
  };

  const propertyDetailsValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        propertyPlan: Yup.object()
          .nullable()
          .required(translate('please-select-property-plan'))
          .test(
            'has-lookup-id',
            translate('please-select-property-plan'),
            (value) => value && value.lookupItemId
          ),
        propertyType: Yup.object()
          .nullable()
          .required(translate('please-select-property-type'))
          .test(
            'has-lookup-id',
            translate('please-select-property-type'),
            (value) => value && value.lookupItemId
          ),
        community: Yup.object()
          .nullable()
          .required(translate('please-select-community'))
          .test(
            'has-lookup-id',
            translate('please-select-community'),
            (value) => value && value.lookupItemId
          ),
      }),
    [translate]
  );

  const getFetchedPropertyDetails = async () => {
    setIsLoading(true);
    const propertyInfoBuffer = context?.propertyInfo;
    if (propertyInfoBuffer && Object.keys(propertyInfoBuffer).length > 0) {
      setSelected({
        id: 'edit',
        value: {
          propertyPlan: propertyInfoBuffer.property_plan || null,
          propertyType: propertyInfoBuffer.property_type || null,
          propertyUsage: propertyInfoBuffer.property_usage || null,
          zoneType: propertyInfoBuffer.zone_type || null,
          ownership: propertyInfoBuffer.ownership || null,
          community: propertyInfoBuffer.community || null,
          urlLocation:
            propertyInfoBuffer.map &&
            propertyInfoBuffer.map.latitude &&
            propertyInfoBuffer.map.longitude
              ? `${propertyInfoBuffer.map.latitude}, ${propertyInfoBuffer.map.longitude}`
              : null,
          numberOfFloors: propertyInfoBuffer.number_of_floors || null,
          completionDate:
            safeHandleDate(propertyInfoBuffer.completion_date) || null,
          numberOfResidentialUnits:
            propertyInfoBuffer.number_of_residential_units || null,
          numberOfCommercialUnits:
            propertyInfoBuffer.number_of_commercial_units || null,
          serviceCharge: propertyInfoBuffer.service_charge || null,
          expectedROI: propertyInfoBuffer.expected_roi || null,
          securityDeposit: propertyInfoBuffer.security_deposit || null,
          availableForNonGCCBuyer: safeHandleNonGCCBuyer(
            propertyInfoBuffer.available_for_non_gcc_buyer
          ),
        },
      });
      setPropertyDetails(propertyInfoBuffer);
    }
    setIsLoading(false);
  };

  const validateForm = useCallback(() => {
    try {
      propertyDetailsValidationSchema.validateSync(selected, {
        abortEarly: false,
      });
      return { isValid: true, errors: [] };
    } catch (error) {
      if (error.inner) {
        return {
          isValid: false,
          errors: error.inner.map((err) => ({
            field: err.path,
            message: err.message,
          })),
        };
      }
      return { isValid: false, errors: [{ message: error.message }] };
    }
  }, [selected, propertyDetailsValidationSchema]);

  const onSaveClicked = useCallback(async () => {
    try {
      await propertyDetailsValidationSchema.validate(selected, {
        abortEarly: false,
      });

      let mapObject = null;
      if (selected.urlLocation && selected.urlLocation.includes(',')) {
        const [lat, lng] = selected.urlLocation
          .split(',')
          .map((coord) => parseFloat(coord.trim()));
        if (!isNaN(lat) && !isNaN(lng)) {
          mapObject = {
            latitude: lat,
            longitude: lng,
          };
        }
      }

      const updatedPropertyData = {
        ...propertyDetails,
        property_plan: selected.propertyPlan,
        property_type: selected.propertyType,
        property_usage: selected.propertyUsage,
        zone_type: selected.zoneType,
        ownership: selected.ownership,
        community: selected.community,
        map:
          selected.urlLocation && selected.urlLocation.includes(',')
            ? {
                latitude: parseFloat(
                  selected.urlLocation.split(',')[0]?.trim()
                ),
                longitude: parseFloat(
                  selected.urlLocation.split(',')[1]?.trim()
                ),
              }
            : null,
        number_of_floors: selected.numberOfFloors,
        completion_date: selected.completionDate
          ? selected.completionDate.format('YYYY-MM-DD')
          : null,
        number_of_residential_units: selected.numberOfResidentialUnits,
        number_of_commercial_units: selected.numberOfCommercialUnits,
        service_charge: selected.serviceCharge,
        expected_roi: selected.expectedROI,
        security_deposit: selected.securityDeposit,
        available_for_non_gcc_buyer: selected.availableForNonGCCBuyer,
      };

      const body = {
        id: propertyId,
        body: {
          propertyJson: {
            property: updatedPropertyData,
          },
          isLocked: updatedPropertyData?.property_lock === 'Yes' ? true : false,
        },
      };

      const res = await propertyDetailsPut(body);
      if (!(res && res.status && res.status !== 200)) {
        setPropertyDetails(updatedPropertyData);
        if (onStepComplete) {
          onStepComplete();
        }
        setAlertBoxContent({
          display: true,
          variant: 'success',
          title: translate('property-details-updated-successfully'),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
        return { success: true };
      } else {
        setAlertBoxContent({
          display: true,
          variant: 'error',
          title: translate('failed-to-update-property-details'),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
        return { success: false, error: 'Failed to update property details' };
      }
    } catch (error) {
      if (error.inner) {
        const errorMessages = error.inner.map((err) => err.message).join(', ');
        setAlertBoxContent({
          display: true,
          variant: 'error',
          title: `${translate('validation-errors')}: ${errorMessages}`,
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      } else {
        setAlertBoxContent({
          display: true,
          variant: 'error',
          title: translate('error-saving-property-details'),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      }
      return { success: false, error: error.message };
    }
  }, [
    selected,
    propertyDetails,
    propertyId,
    propertyDetailsValidationSchema,
    translate,
    setAlertBoxContent,
    onStepComplete,
  ]);

  const onNextClicked = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      const errorMessages = validation.errors
        .map((err) => err.message)
        .join(', ');
      setAlertBoxContent({
        display: true,
        variant: 'error',
        title: `${translate('validation-errors')}: ${errorMessages}`,
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
      return { success: false, errors: validation.errors };
    }

    const result = await onSaveClicked();
    
    if (result && result.success && context?.refetchPropertyInfo) {
      await context.refetchPropertyInfo();
    }
    
    return result;
  }, [onSaveClicked, validateForm, translate, setAlertBoxContent, context]);

  useEffect(() => {
    if (context?.setSaveHandler) context.setSaveHandler(onSaveClicked);
    if (context?.setNextHandler) context.setNextHandler(onNextClicked);
    if (context?.setValidationHandler)
      context.setValidationHandler(validateForm);

    return () => {
      if (context?.setSaveHandler) context.setSaveHandler(null);
      if (context?.setNextHandler) context.setNextHandler(null);
      if (context?.setValidationHandler) context.setValidationHandler(null);
    };
  }, [onSaveClicked, onNextClicked, validateForm, context]);

  const getPropertyPlansLookups = async () => {
    const res = await GetLookupItemsByLookupTypeName({
      lookUpName: 'Property Plan',
      pageSize: filter.pageSize,
      pageIndex: filter.pageIndex,
    });

    if (!(res && res.status && res.status !== 200)) {
      const lookupData = res.result || [];
      setData({ id: 'propertyPlans', value: lookupData });

      if (propertyDetails.property_plan) {
        const matchedPlan = getNewValue(
          'propertyPlans',
          propertyDetails.property_plan,
          lookupData
        );
        if (matchedPlan) {
          setSelected({ id: 'propertyPlan', value: matchedPlan });
        }
      }
    } else {
      setData({ id: 'propertyPlans', value: [] });
    }
  };

  const getPropertyTypesLookups = async () => {
    const res = await GetLookupItemsByLookupTypeName({
      lookUpName: 'PropertyType',
      pageSize: filter.pageSize,
      pageIndex: filter.pageIndex,
    });

    if (!(res && res.status && res.status !== 200)) {
      const lookupData = res.result || [];
      setData({ id: 'propertyTypes', value: lookupData });

      if (propertyDetails.property_type) {
        const matchedType = getNewValue(
          'propertyTypes',
          propertyDetails.property_type,
          lookupData
        );
        if (matchedType) {
          setSelected({ id: 'propertyType', value: matchedType });
        }
      }
    } else {
      setData({ id: 'propertyTypes', value: [] });
    }
  };

  const getPropertyUsageLookups = async () => {
    const res = await GetLookupItemsByLookupTypeName({
      lookUpName: 'Property Usage',
      pageSize: filter.pageSize,
      pageIndex: filter.pageIndex,
    });

    if (!(res && res.status && res.status !== 200)) {
      const lookupData = res.result || [];
      setData({ id: 'propertyUsages', value: lookupData });

      if (propertyDetails.property_usage) {
        const matchedUsage = getNewValue(
          'propertyUsages',
          propertyDetails.property_usage,
          lookupData
        );
        if (matchedUsage) {
          setSelected({ id: 'propertyUsage', value: matchedUsage });
        }
      }
    } else {
      setData({ id: 'propertyUsages', value: [] });
    }
  };

  const getPropertyZoneTypesLookups = async () => {
    const res = await GetLookupItemsByLookupTypeName({
      lookUpName: 'PropertyLicensing',
      pageSize: filter.pageSize,
      pageIndex: filter.pageIndex,
    });

    if (!(res && res.status && res.status !== 200)) {
      const lookupData = res.result || [];
      setData({ id: 'zoneTypes', value: lookupData });

      if (propertyDetails.zone_type) {
        const matchedZoneType = getNewValue(
          'zoneTypes',
          propertyDetails.zone_type,
          lookupData
        );
        if (matchedZoneType) {
          setSelected({ id: 'zoneType', value: matchedZoneType });
        }
      }
    } else {
      setData({ id: 'zoneTypes', value: [] });
    }
  };

  const getAllCommunitiesLookups = async (searchTerm = '') => {
    const res = await GetLookupItemsByLookupTypeName({
      lookUpName: 'Community',
      pageSize: searchTerm ? 500 : filter.pageSize,
      pageIndex: filter.pageIndex,
      searchTerm: searchTerm,
    });

    if (!(res && res.status && res.status !== 200)) {
      const lookupData = res.result || [];
      setData({ id: 'communities', value: lookupData });

      if (propertyDetails.community) {
        const matchedCommunity = getNewValue(
          'communities',
          propertyDetails.community,
          lookupData
        );
        if (matchedCommunity) {
          setSelected({ id: 'community', value: matchedCommunity });
        }
      }
    } else {
      setData({ id: 'communities', value: [] });
    }
  };

  const getAllOwnershipsLookups = async () => {
    const res = await GetLookupItemsByLookupTypeName({
      lookUpName: 'PropertyOwnership',
      pageSize: filter.pageSize,
      pageIndex: filter.pageIndex,
    });

    if (!(res && res.status && res.status !== 200)) {
      const lookupData = res.result || [];
      setData({ id: 'ownerships', value: lookupData });

      if (propertyDetails.ownership) {
        const matchedOwnership = getNewValue(
          'ownerships',
          propertyDetails.ownership,
          lookupData
        );
        if (matchedOwnership) {
          setSelected({ id: 'ownership', value: matchedOwnership });
        }
      }
    } else {
      setData({ id: 'ownerships', value: [] });
    }
  };

  const getNewValue = (key, value, lookupData = null) => {
    const list = lookupData || data[key] || [];

    if (!value) return null;

    if (typeof value === 'object' && value.lookupItemId) {
      return (
        list.find((item) => item.lookupItemId === value.lookupItemId) || null
      );
    }

    if (typeof value === 'number') {
      return list.find((item) => item.lookupItemId === value) || null;
    }

    if (typeof value === 'string') {
      return list.find((item) => item.lookupItemName === value) || null;
    }

    return null;
  };

  const fetchAllLookups = async () => {
    if (!propertyDetails || Object.keys(propertyDetails).length === 0) {
      return;
    }

    try {
      await getPropertyPlansLookups();
      await getPropertyTypesLookups();
      await getPropertyUsageLookups();
      await getAllCommunitiesLookups();
      await getPropertyZoneTypesLookups();
      await getAllOwnershipsLookups();

      setIsReady(true);
    } catch (error) {
      setIsReady(true);
    }
  };

  useEffect(() => {
    if (propertyId && context?.isFetched) {
      getFetchedPropertyDetails();
    }
  }, [propertyId, context?.propertyInfo, context?.isFetched]);

  useEffect(() => {
    if (!isLoading && !isReady) {
      if (propertyDetails && Object.keys(propertyDetails).length > 0) {
        fetchAllLookups();
      } else {
        setIsReady(true);
      }
    }
  }, [propertyDetails, isLoading, isReady]);

  return isLoading || !isReady ? (
    <CPP_PropertyDetailsSkeleton />
  ) : (
    <div className={styles.completePropertyProfilePageWrapper}>
      <CPP_PD_BasicInformationSection
        data={data}
        getNewValue={getNewValue}
        selected={selected}
        setSelected={setSelected}
        handleSelectedChange={handleSelectedChange}
        handleMultipleSelectedChange={handleMultipleSelectedChange}
        safeHandleDate={safeHandleDate}
        safeHandleNonGCCBuyer={safeHandleNonGCCBuyer}
        translate={translate}
      />
      <CPP_BI_PropertySpecificationSection
        data={data}
        selected={selected}
        setSelected={setSelected}
        handleSelectedChange={handleSelectedChange}
        handleMultipleSelectedChange={handleMultipleSelectedChange}
        translate={translate}
      />
      <CPP_BI_FinancialInformation
        data={data}
        selected={selected}
        setSelected={setSelected}
        handleSelectedChange={handleSelectedChange}
        handleMultipleSelectedChange={handleMultipleSelectedChange}
        safeHandleDate={safeHandleDate}
        translate={translate}
      />
      <CPP_BI_NonGCCbuyers
        data={data}
        selected={selected}
        setSelected={setSelected}
        handleSelectedChange={handleSelectedChange}
        handleMultipleSelectedChange={handleMultipleSelectedChange}
        safeHandleNonGCCBuyer={safeHandleNonGCCBuyer}
        translate={translate}
      />
    </div>
  );
}

export default CPP_PropertyDetailsComponent;
